import React from 'react'
import { Box, TextField, InputAdornment } from '@mui/material'
import { INTERAX_COLORS } from '../constants/colors'

export interface InputProps {
  /** Input value */
  value: string
  /** Input change handler */
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  /** Placeholder text */
  placeholder?: string
  /** Input type */
  type?: 'text' | 'email' | 'password' | 'number' | 'search'
  /** Whether the input is disabled */
  disabled?: boolean
  /** Whether the input is in error state */
  error?: boolean
  /** Error message to display */
  errorMessage?: string
  /** Whether the input is required */
  required?: boolean
  /** Maximum length of input */
  maxLength?: number
  /** Icon to display at the start of the input */
  startIcon?: React.ReactNode
  /** Icon to display at the end of the input */
  endIcon?: React.ReactNode
  /** Click handler for end icon */
  onEndIconClick?: () => void
  /** Additional CSS classes */
  className?: string
  /** Full width of the input */
  fullWidth?: boolean
  /** Size of the input */
  size?: 'small' | 'medium' | 'large'
  /** Auto focus the input */
  autoFocus?: boolean
  /** Callback when Enter key is pressed */
  onEnterPress?: () => void
  /** Wrap over several lines instead of scrolling sideways — a narrow single-line box hides the start. */
  multiline?: boolean
  /** Lines to grow to before the field starts scrolling. Only meaningful with `multiline`. */
  maxRows?: number
  /** Blur handler (e.g. touch validation) */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  /** Test id for e2e selectors */
  'data-testid'?: string
}

/**
 * Input Component
 *
 * A design system input component built on Material UI TextField with Interax styling.
 * Supports icons, validation states, and various input types.
 *
 * @param props - Props for the Input component
 */
const Input: React.FC<InputProps> = ({
  value,
  onChange,
  placeholder,
  type = 'text',
  disabled = false,
  error = false,
  errorMessage,
  required = false,
  maxLength,
  startIcon,
  endIcon,
  onEndIconClick,
  className = '',
  fullWidth = false,
  size = 'medium',
  autoFocus = false,
  onEnterPress,
  onBlur,
  multiline = false,
  maxRows,
  'data-testid': dataTestId
}) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || !onEnterPress) return
    // In a multiline box Enter is a newline unless it is the submit gesture; Shift+Enter always wraps.
    if (multiline && event.shiftKey) return
    if (multiline) event.preventDefault()
    onEnterPress()
  }

  const getSizeProps = () => {
    switch (size) {
      case 'small':
        return { height: '36px', fontSize: '14px' }
      case 'large':
        return { height: '48px', fontSize: '16px' }
      default:
        return { height: '42px', fontSize: '16px' }
    }
  }

  const sizeProps = getSizeProps()

  return (
    <Box className={className} sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <TextField
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        type={type}
        disabled={disabled}
        required={required}
        inputProps={{ maxLength, 'data-testid': dataTestId }}
        autoFocus={autoFocus}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        multiline={multiline}
        maxRows={maxRows}
        fullWidth={fullWidth}
        variant="outlined"
        error={error}
        helperText={error ? errorMessage : ''}
        sx={{
          '& .MuiOutlinedInput-root': {
            // A wrapping field has to be free to grow; a fixed height would clip the second line.
            ...(multiline ? { minHeight: sizeProps.height } : { height: sizeProps.height }),
            backgroundColor: INTERAX_COLORS.offBlack,
            borderColor: error ? INTERAX_COLORS.redError : INTERAX_COLORS.thunderGrey,
            color: INTERAX_COLORS.white,
            fontSize: sizeProps.fontSize,
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: error ? INTERAX_COLORS.redError : INTERAX_COLORS.sun
              }
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: error ? INTERAX_COLORS.redError : INTERAX_COLORS.sun,
                borderWidth: '2px'
              }
            },
            '&.Mui-disabled': {
              backgroundColor: INTERAX_COLORS.thunderGrey20,
              color: INTERAX_COLORS.thunderGrey
            }
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: error ? INTERAX_COLORS.redError : INTERAX_COLORS.thunderGrey,
            borderRadius: '8px'
          },
          '& .MuiInputBase-input': {
            color: INTERAX_COLORS.white,
            fontSize: sizeProps.fontSize,
            padding: size === 'small' ? '8px 12px' : '12px 16px',
            '&::placeholder': {
              color: INTERAX_COLORS.thunderGrey,
              opacity: 1
            }
          },
          '& .MuiFormHelperText-root': {
            color: INTERAX_COLORS.redError,
            fontSize: '12px',
            marginTop: '4px'
          }
        }}
        InputProps={{
          startAdornment: startIcon ? (
            <InputAdornment position="start" sx={{ color: INTERAX_COLORS.thunderGrey }}>
              {startIcon}
            </InputAdornment>
          ) : undefined,
          endAdornment: endIcon ? (
            <InputAdornment
              position="end"
              sx={{
                color: INTERAX_COLORS.thunderGrey,
                cursor: onEndIconClick ? 'pointer' : 'default'
              }}
              onClick={onEndIconClick}
            >
              {endIcon}
            </InputAdornment>
          ) : undefined
        }}
      />
    </Box>
  )
}

export default Input
